import {
  EVENT_USER_LANGUAGE_CHANGED,
  EVENT_USER_PROFILE_CHANGED,
} from "@rio-cloud/rio-user-menu-component";
import { store } from "@store";
import "moment/dist/locale/en-gb";
import "moment/dist/locale/es";
import "moment/dist/locale/pt-br";
import type { UserManager } from "oidc-client-ts";
import { getLocale } from "src/store/lang/langSlice";
import { config } from "../../config";
import {
  userProfileObtained,
  userSessionExpired,
  userSessionRenewed,
} from "../../store/login/LoginSlice";
import { accessTokenStored, idTokenStored } from "../../store/token/TokenSlice";
import { extractLanguage } from "./lang/lang";
import { configureFetchDisplayMessages } from "./lang/services";
import {
  configureMockUserManager,
  configureUserManager,
  createUserManager,
  type SessionRenewedResult,
} from "./login/login";
import { attemptInitialSignIn } from "./setup/oauth";
import { trace } from "./setup/trace";
import { accessToken } from "./tokenHandling/accessToken";

export interface OAuthConfig {
  onSessionExpired: Function;
  onSessionRenewed: Function;
}

export const main = async (renderApp: () => void) => {
  const fetchDisplayMessages = configureFetchDisplayMessages(store);

  // We want the `<html lang>` attribute to be synced with the
  // language currently displayed
  store.subscribe(() => {
    const lang = extractLanguage(getLocale(store.getState()));
    const html = document.querySelector("html");

    if (html && lang && typeof lang === 'string' && html.getAttribute("lang") !== lang) {
      html.setAttribute("lang", lang);
    }
  });

  const oauthConfig = {
    onSessionExpired: () => {
      accessToken.discardAccessToken();
      store.dispatch(userSessionExpired());
    },
    onSessionRenewed: (result: SessionRenewedResult) => {
      trace("index.onTokenRenewed", result);
      accessToken.saveAccessToken(result.accessToken);

      // Salvar refresh token se disponível
      if (result.refreshToken) {
        (accessToken as any).saveRefreshToken(result.refreshToken);
      }

      store.dispatch(accessTokenStored(result.accessToken));
      store.dispatch(idTokenStored(result.idToken));
      store.dispatch(userProfileObtained({
        ...result.profile,
        userId: result?.profile?.sub?.split(':')[1]
      }));

      store.dispatch(userSessionRenewed());
      fetchDisplayMessages(result.locale);
    },
  } as OAuthConfig;

  const userManager: UserManager = config.login.mockAuthorization
    ? configureMockUserManager(oauthConfig)
    : configureUserManager(oauthConfig, createUserManager());

  // Expor userManager globalmente para uso nos interceptors
  (window as any).userManager = userManager;

  const signinSilent = userManager.signinSilent.bind(userManager);
  document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, () => signinSilent());
  document.addEventListener(EVENT_USER_PROFILE_CHANGED, () => signinSilent());

  try {
    await userManager.clearStaleState();
    await attemptInitialSignIn(userManager);
    renderApp();
  } catch (error) {
    trace("could not start application", error);
    // reportErrorToSentry(error);
  }
};
